import { useState, useEffect, useMemo, useRef, useCallback } from "react";
// import config from "../../config";
import useAxios from "../../utils/useAxios";
import { useAppContext } from "../../context/AppContext";
import AsyncSelect from "react-select/async";
import { hideModal, showModal } from "../../utils/modalUtility";

function DonorGroups() {
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);

  const [groupDonor, setGroupDonor] = useState([]);
  const [role, setRole] = useState(null);
  const { state } = useAppContext();

  const [selectedOption, setSelectedOption] = useState([]);

  const [formData, setFormData] = useState({
    program_name: "",
    donor_ids: [], // Make sure this is initialized as an empty array
  });

  const axiosAPI = useAxios(); // Use the custom hook for Axios
  const debounceRef = useRef(null);

  // Requirement-compatible local data source
  const data = Array.isArray(state?.data) ? state.data : [];

  const buildOption = useCallback((item) => {
    const fullName = String(item?.full_name || "").trim();
    const phoneNumber = String(item?.phone_number || "").trim();
    const donorId = String(item?.donor_id || "").trim();
    const userId = item?.id;

    if (userId == null || !fullName) return null;

    return {
      value: userId,
      label: `${fullName} - ${phoneNumber || "N/A"} - ${donorId || "N/A"}`,
      fullNameLower: fullName.toLowerCase(),
      phoneNumber,
    };
  }, []);

  const allOptions = useMemo(
    () => data.map(buildOption).filter(Boolean),
    [data, buildOption],
  );

  const defaultDonorOptions = useMemo(
    () => allOptions.slice(0, 50),
    [allOptions],
  );

  const loadOptions = useCallback(
    (inputValue, callback) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        const query = String(inputValue || "")
          .trim()
          .toLowerCase();

        if (!query) {
          callback(defaultDonorOptions);
          return;
        }

        const filtered = allOptions
          .filter(
            (option) =>
              option.fullNameLower.includes(query) ||
              option.phoneNumber.includes(inputValue),
          )
          .slice(0, 50);

        callback(filtered);
      }, 300);
    },
    [allOptions, defaultDonorOptions],
  );

  useEffect(
    () => () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    },
    [],
  );

  const removeFormData = () => {
    setFormData({
      program_name: "",
      donor_ids: [], // Make sure this is initialized as an empty array
    });
  };

  // Fetch Group Donor
  const fetchGroupDonor = async () => {
    try {
      const response = await axiosAPI.get("/api/group/donor/get-group-donor");
      const groupDonorData = response.data.results;
      setGroupDonor(groupDonorData);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching classes:", error.message);
    }
  };

  useEffect(() => {
    // const role = JSON.parse(localStorage.getItem("user_role")); // Parses string "admin" to plain string
    const role = state?.user?.role; // Parses string "admin" to plain string
    setRole(role);
  }, []);

  // Initialize on Component Mount
  useEffect(() => {
    fetchGroupDonor();
  }, []);

  // console.log("user",users)

  // Handle Input Changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  // Handle Submit (Add or Edit)
  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      program_name: formData.program_name,
      donor_ids: formData.donor_ids,
    };

    // console.log("Payload", payload);

    try {
      let response;
      if (isEditMode) {
        response = await axiosAPI.put(
          `/api/group/donor/update-group-donor/${currentUserId}`,
          payload,
        );
        window.notifyMessage(
          "Success",
          "Donor Details Updated successfully",
          "success",
        );
        hideModal("newUserModal");
        fetchGroupDonor();
      } else {
        response = await axiosAPI.post(
          `/api/group/donor/add-donor-group`,
          payload,
        );
        window.notifyMessage("Success", "Donor successfully added", "success");
        closeModal();
        hideModal("newUserModal");
      }
      removeFormData();
      // window.location.reload(); // Reload page to reflect changes in the user list
      // fetchUsers();
      fetchGroupDonor();
      // console.log("Emnpty form data");
      closeModal();
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        window.notifyMessage(
          "Error",
          error.response.data.message, // Display specific error message from backend
          "danger",
        );
      } else {
        // Handle generic errors (e.g., network errors)
        window.notifyMessage("Error", "There was an error!", "danger");
      }
    }
  };

  // Edit Modal Open
  const openEditModal = (userId) => {
    setIsEditMode(true);
    // console.log("user id", userId);
    const userToEdit = groupDonor.find((user) => user.group_id === userId);
    // console.log("Open edit modal", userToEdit);

    if (userToEdit) {
      // console.log({ userToEdit });

      const parsedDonorIds = String(userToEdit.donor_ids || "")
        .split(",")
        .map((id) => Number(id.trim()))
        .filter((id) => !Number.isNaN(id));

      // console.log({ parsedDonorIds });

      const normalizedGroupDonorNames = (userToEdit.donor_names || "")
        .split(",")
        .map((name) => name.trim().toLowerCase())
        .filter(Boolean);

      // console.log({ normalizedGroupDonorNames });

      const hasParsedIds = parsedDonorIds.length > 0;

      const selectedSubjects = data.filter((donorItem) => {
        const donorItemId = Number(donorItem?.id);
        if (hasParsedIds) {
          return parsedDonorIds.includes(donorItemId);
        }

        const donorName = String(donorItem?.full_name || "")
          .trim()
          .toLowerCase();
        return donorName && normalizedGroupDonorNames.includes(donorName);
      });

      // console.log(selectedSubjects);

      // Prevent duplicate selected chips in edit mode.
      const uniqueById = Array.from(
        new Map(selectedSubjects.map((item) => [item.id, item])).values(),
      );

      // console.log("Unique by ID", uniqueById);

      const subjectsId = uniqueById.map((item) => item.id);

      const selectedDonorOptions = uniqueById.map(buildOption).filter(Boolean);

      // console.log("Selected subjects", subjectsId);

      setFormData({
        program_name: userToEdit.donor_group_name,
        donor_ids: subjectsId,
      });
      setSelectedOption(selectedDonorOptions);

      // return;

      setCurrentUserId(userToEdit.group_id);
      // modal.show();
      showModal("newUserModal");
    }
  };

  // Close Modal
  const closeModal = () => {
    setIsEditMode(false);
    removeFormData();
    setSelectedOption([]);
    setCurrentUserId(null);
  };

  const openAddModal = () => {
    setIsEditMode(false);
    removeFormData();
    setSelectedOption([]);

    // Optionally, you can open the modal programmatically here if you're using Bootstrap modals
    showModal("newUserModal");
  };

  const handleDelete = async (id) => {
    const isConfirmed = window.confirm("Are you sure you want to delete this?");
    if (!isConfirmed) return;

    try {
      const response = await axiosAPI.delete(
        `/api/group/donor/delete-group-donor/${id}`,
      );
      window.notifyMessage("Success", "Deleted successfully", "success");
      fetchGroupDonor();
    } catch (error) {
      console.error("Error deleting:", error);
      window.notifyMessage(
        "Error",
        "An error occurred while deleting.",
        "danger",
      );
    }
  };

  return (
    <div className="container">
      <div className="page-inner">
        <div className="d-flex justify-content-end align-items-end flex-column flex-md-row">
          <div>
            <button
              type="button"
              className="btn btn-dark btn-round mb-4"
              // data-bs-toggle="modal"
              // data-bs-target="#newUserModal"
              onClick={() => openAddModal()}
            >
              New Group
            </button>
          </div>
        </div>

        <div
          className="modal fade"
          id="newUserModal"
          tabIndex="-1"
          aria-labelledby="newUserModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="newUserModalLabel">
                  {isEditMode ? "Edit Group" : "Add New Group"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={closeModal}
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  {/* First Name */}

                  <div className="mb-3">
                    <label htmlFor="program_name" className="form-label">
                      Group Name
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="program_name"
                      value={formData.program_name}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  {/* Subjects */}
                  <div className="mb-3">
                    <label htmlFor="donor_ids" className="form-label">
                      Donors
                    </label>
                    <AsyncSelect
                      isMulti
                      cacheOptions
                      defaultOptions={defaultDonorOptions}
                      loadOptions={loadOptions}
                      value={selectedOption}
                      onChange={(selected) => {
                        const normalizedSelected = selected || [];
                        setSelectedOption(normalizedSelected);
                        setFormData((prev) => ({
                          ...prev,
                          donor_ids: normalizedSelected.map(
                            (item) => item.value,
                          ),
                        }));
                      }}
                      placeholder="Search Donor"
                      isSearchable
                      isClearable
                      classNamePrefix="react-select"
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary btn-round btn-sm"
                  >
                    {isEditMode ? "Save Group" : "Add Group"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Student Table */}
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header">
                <h4 className="card-title">
                  Group Donors ({groupDonor.length})
                </h4>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Group Name</th>
                        <th>Donors</th>
                        {/* <th>Action</th> */}
                        {role === "admin" && (
                          <th>Action</th> // Show this if the role is "admin"
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="4" className="text-center">
                            Loading...
                          </td>
                        </tr>
                      ) : (
                        groupDonor.map((donor) => (
                          <tr key={donor.group_id}>
                            <td>{donor.donor_group_name || "not"} </td>
                            {donor.donor_names ? (
                              <td>
                                {donor.donor_names
                                  .split(",")
                                  .slice(0, 5)
                                  .map((subject, index) => (
                                    <span
                                      key={index}
                                      className="badge rounded-pill bg-light text-dark me-1"
                                    >
                                      {subject.trim()}
                                    </span>
                                  ))}

                                {donor.donor_names.split(",").length > 5 && (
                                  <span className="badge rounded-pill bg-white text-dark ">
                                    +...
                                  </span>
                                )}
                              </td>
                            ) : (
                              <td>No Donor assigned</td>
                            )}

                            <td>
                              <button
                                className="btn btn-icon btn-round btn-dark btn-sm me-2"
                                onClick={() => openEditModal(donor.group_id)}
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              {/* <button
                                className="btn btn-icon btn-round btn-danger btn-sm me-2"
                                onClick={() => handleDelete(donor.id)}
                              >
                                <i className="fas fa-trash"></i>
                              </button> */}
                              {/* {role === "admin" && (
                                <button
                                  className="btn btn-icon btn-round btn-danger btn-sm me-2"
                                  onClick={() => handleDelete(donor.id)}
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              )} */}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DonorGroups;
